import { User, ChevronDown } from 'lucide-react';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import livelinxLogo from '@/assets/images/livelinx-auth-logo.png';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { cn } from '@/lib/utils';

import defaultConfig from '../config/header-config.json';
import { useAppHeader } from '../hooks/useAppHeader';
import { AppHeaderProps, AppHeaderConfig } from '../types';

const MarqueeBreadcrumbs = ({ children }: { children: React.ReactNode }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);

  const checkOverflow = useCallback(() => {
    if (wrapperRef.current && innerRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth;
      const innerWidth = innerRef.current.scrollWidth;
      const overflow = innerWidth - wrapperWidth;
      const isOverflowing = overflow > 5;
      setShouldAnimate(isOverflowing && !userScrolled);
      if (isOverflowing) {
        innerRef.current.style.setProperty('--scroll-distance', `-${overflow + 10}px`);
      }
    }
  }, [userScrolled]);

  useEffect(() => {
    checkOverflow();
    const timer = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timer);
    };
  }, [checkOverflow, children]);

  const handleScroll = useCallback(() => {
    setUserScrolled(true);
    setShouldAnimate(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setTimeout(() => {
      setUserScrolled(false);
      checkOverflow();
    }, 3000);
  }, [checkOverflow]);

  return (
    <div
      ref={wrapperRef}
      className="marquee-wrapper"
      onScroll={handleScroll}
      onTouchEnd={handleTouchEnd}
    >
      <div ref={innerRef} className={cn('marquee-inner', shouldAnimate && 'animate')}>
        {children}
      </div>
    </div>
  );
};

export const AppHeader = ({ userProfile, config: configOverride }: AppHeaderProps) => {
  // Merge default config with overrides
  const config = {
    ...defaultConfig,
    ...configOverride,
    display: { ...defaultConfig.display, ...configOverride?.display },
    logo: { ...defaultConfig.logo, ...configOverride?.logo },
    userMenu: { ...defaultConfig.userMenu, ...configOverride?.userMenu },
    breadcrumb: { ...defaultConfig.breadcrumb, ...configOverride?.breadcrumb },
    styles: { ...defaultConfig.styles, ...configOverride?.styles },
  } satisfies AppHeaderConfig;

  const { breadcrumbs, displayName, initials, handleSignOut, navigateHome, navigate } =
    useAppHeader(userProfile, config);

  // Get masterclass context for dynamic logo
  const { selectedMasterclass, clientLogoUrl } = useMasterclass();

  // Use LiveLinx logo by default, client logo only inside a masterclass
  const isInsideMasterclass = !!selectedMasterclass;
  const currentLogo = isInsideMasterclass && clientLogoUrl ? clientLogoUrl : livelinxLogo;
  const logoAlt = isInsideMasterclass ? 'Client' : 'LiveLinx';

  return (
    <header
      className={`bg-${config.styles.backgroundColor} border-b border-${config.styles.borderColor} flex items-center justify-between px-6 py-3`}
    >
      {/* Logo et Breadcrumb */}
      <div className="flex min-w-0 flex-1 items-center gap-6">
        {config.display.showLogo && (
          <ImageWithLoader
            src={currentLogo}
            alt={logoAlt}
            className="h-8 w-auto flex-shrink-0 cursor-pointer"
            containerClassName="flex-shrink-0"
            skeletonClassName="h-8 w-24"
            loading="eager"
            onClick={
              isInsideMasterclass
                ? navigateHome // Navigate to case selector when inside masterclass
                : () => window.open('https://www.livelinx.com', '_blank')
            }
          />
        )}

        {config.display.showBreadcrumb && (
          <Breadcrumb className="min-w-0 flex-1">
            {/* Desktop: normal truncate behavior */}
            <BreadcrumbList className="hidden flex-nowrap md:flex">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 && <BreadcrumbSeparator className="mx-1 flex-shrink-0" />}
                  <BreadcrumbItem className="min-w-0">
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage
                        className={`text-${config.styles.textColor} truncate font-semibold`}
                      >
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => navigate(crumb.path)}
                        className="cursor-pointer truncate text-livelinx-grey200 hover:text-livelinx-text"
                      >
                        {crumb.label}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
            </BreadcrumbList>

            {/* Mobile: marquee scrolling behavior */}
            <div className="md:hidden">
              <MarqueeBreadcrumbs>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.path}>
                    {index > 0 && (
                      <span className="mx-1 flex-shrink-0 text-muted-foreground">/</span>
                    )}
                    {index === breadcrumbs.length - 1 ? (
                      <span
                        className={`text-${config.styles.textColor} whitespace-nowrap font-semibold`}
                      >
                        {crumb.label}
                      </span>
                    ) : (
                      <span
                        onClick={() => navigate(crumb.path)}
                        className="cursor-pointer whitespace-nowrap text-livelinx-grey200"
                      >
                        {crumb.label}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </MarqueeBreadcrumbs>
            </div>
          </Breadcrumb>
        )}
      </div>

      {/* Menu utilisateur */}
      {config.display.showUserMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`flex items-center gap-2 hover:bg-${config.styles.hoverColor} h-10`}
            >
              {config.userMenu.showAvatar && (
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" alt={displayName} />
                  <AvatarFallback className="bg-livelinx-blue300 text-xs font-semibold text-white">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              )}
              {config.userMenu.showName && (
                <span
                  className={`text-${config.styles.textColor} hidden truncate text-sm font-semibold md:inline`}
                  style={{ maxWidth: `${config.userMenu.maxNameLength}px` }}
                >
                  {displayName}
                </span>
              )}
              {config.userMenu.showDropdownIcon && (
                <ChevronDown className="h-4 w-4 text-livelinx-grey200" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={`w-48 bg-${config.styles.dropdownBackground} ${config.styles.dropdownShadow} border-${config.styles.borderColor} z-dropdown`}
          >
            <DropdownMenuItem
              onClick={() => void handleSignOut()}
              className={`text-${config.styles.textColor} hover:bg-${config.styles.hoverColor} cursor-pointer hover:text-livelinx-blue300`}
            >
              <User className="mr-2 h-4 w-4" />
              <span>{config.userMenu.logoutLabel}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
};
